import type { FeedbackThread } from "@/lib/feedback/types"

// Shared, DOM-only helpers for locating a thread's quoted text inside rendered
// prose. Used by both the admin inline review layer (InlineThreadsLayer) and the
// reader-page "feedback you submitted" layer (SubmittedFeedbackLayer).

// Minimum length (in normalized alphanumerics) before we try to anchor a quote.
// Very short quotes match too loosely to place reliably.
export const MIN_QUOTE_LENGTH = 4

export type NormalizedEntry = {
  node: Text
  normalizedStart: number
  charIndexInNode: number[]
}

export type NormalizedIndex = {
  normalized: string
  entries: NormalizedEntry[]
}

export type QuoteMatch = {
  thread: FeedbackThread
  normalizedStart: number
  normalizedEnd: number
}

export const toAlphanumericLower = (text: string) =>
  text.toLowerCase().replace(/[^a-z0-9]/g, "")

// Walks a container's text nodes and builds a normalized (lowercase
// alphanumeric-only) string alongside a map back to the original DOM offsets, so
// a normalized match index can be resolved to a precise (node, offset) pair.
export const buildNormalizedIndex = (container: HTMLElement): NormalizedIndex => {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)

  const entries: NormalizedEntry[] = []
  let normalized = ""

  let currentNode = walker.nextNode() as Text | null
  while (currentNode) {
    const rawText = currentNode.nodeValue ?? ""
    const charIndexInNode: number[] = []
    const start = normalized.length
    for (let i = 0; i < rawText.length; i++) {
      const code = rawText.charCodeAt(i)
      const isLower = code >= 97 && code <= 122
      const isUpper = code >= 65 && code <= 90
      const isDigit = code >= 48 && code <= 57
      if (isLower || isDigit) {
        charIndexInNode.push(i)
        normalized += rawText[i]
      } else if (isUpper) {
        charIndexInNode.push(i)
        normalized += rawText[i].toLowerCase()
      }
    }
    if (charIndexInNode.length > 0) {
      entries.push({ node: currentNode, normalizedStart: start, charIndexInNode })
    }
    currentNode = walker.nextNode() as Text | null
  }

  return { normalized, entries }
}

export const findEntryForNormalizedIndex = (
  entries: NormalizedEntry[],
  normalizedIndex: number
) => {
  let low = 0
  let high = entries.length - 1
  while (low <= high) {
    const mid = (low + high) >>> 1
    const entry = entries[mid]
    const entryEnd = entry.normalizedStart + entry.charIndexInNode.length
    if (normalizedIndex < entry.normalizedStart) {
      high = mid - 1
    } else if (normalizedIndex >= entryEnd) {
      low = mid + 1
    } else {
      const offsetInEntry = normalizedIndex - entry.normalizedStart
      return {
        entry,
        offsetInNode: entry.charIndexInNode[offsetInEntry],
      }
    }
  }
  return null
}

export const findQuoteMatches = (
  threads: FeedbackThread[],
  normalized: string
): QuoteMatch[] => {
  const matches: QuoteMatch[] = []
  for (const thread of threads) {
    if (!thread.selectedText) continue
    const quote = toAlphanumericLower(thread.selectedText)
    if (quote.length < MIN_QUOTE_LENGTH) continue
    const start = normalized.indexOf(quote)
    if (start === -1) continue
    matches.push({
      thread,
      normalizedStart: start,
      normalizedEnd: start + quote.length,
    })
  }
  return matches
}

export const buildRangeForMatch = (
  entries: NormalizedEntry[],
  match: QuoteMatch
): Range | null => {
  const startResolved = findEntryForNormalizedIndex(entries, match.normalizedStart)
  const endResolved = findEntryForNormalizedIndex(entries, match.normalizedEnd - 1)
  if (!startResolved || !endResolved) return null

  const range = document.createRange()
  range.setStart(startResolved.entry.node, startResolved.offsetInNode)
  range.setEnd(endResolved.entry.node, endResolved.offsetInNode + 1)
  return range
}
